import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lightning, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuickCommercePage() {
  const [config, setConfig] = useState({ enabled: false, sla_minutes: 30, priority: 'high', max_distance_km: 10.0, surge_multiplier: 1.0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/quick-commerce-config`, { withCredentials: true });
      setConfig(data);
    } catch { /* defaults */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/seller/quick-commerce-config`, config, { withCredentials: true });
      toast.success('Quick commerce config saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout><div className="p-6 text-center text-[#9c9db7]">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl" data-testid="quick-commerce-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Quick Commerce</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Configure express delivery SLA and priority settings</p>
          </div>
          <Button data-testid="save-qc-btn" onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <FloppyDisk size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Config'}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lightning size={24} className={config.enabled ? 'text-[#7d64ff]' : 'text-[#9c9db7]'} />
                <div>
                  <p className="font-semibold text-[#0A0A0A]">Enable Quick Commerce</p>
                  <p className="text-xs text-[#6b6b8a]">Accept ultra-fast delivery orders with separate SLA</p>
                </div>
              </div>
              <button onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))} className={`w-12 h-6 rounded-full transition-colors relative ${config.enabled ? 'bg-[#7d64ff]' : 'bg-gray-300'}`} data-testid="qc-toggle">
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {config.enabled && (
            <>
              <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
                <Label className="text-sm font-semibold">SLA (minutes)</Label>
                <p className="text-xs text-[#9c9db7] mb-3">Maximum time to deliver a quick-commerce order</p>
                <Input data-testid="qc-sla" type="number" value={config.sla_minutes} onChange={e => setConfig(c => ({ ...c, sla_minutes: parseInt(e.target.value) || 30 }))} className="max-w-[200px]" />
              </div>

              <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
                <Label className="text-sm font-semibold">Priority Level</Label>
                <p className="text-xs text-[#9c9db7] mb-3">Order processing priority for quick commerce</p>
                <select data-testid="qc-priority" value={config.priority} onChange={e => setConfig(c => ({ ...c, priority: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
                <Label className="text-sm font-semibold">Max Distance (km)</Label>
                <p className="text-xs text-[#9c9db7] mb-3">Maximum delivery radius for quick commerce orders</p>
                <Input data-testid="qc-distance" type="number" step="0.5" value={config.max_distance_km} onChange={e => setConfig(c => ({ ...c, max_distance_km: parseFloat(e.target.value) || 10 }))} className="max-w-[200px]" />
              </div>

              <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
                <Label className="text-sm font-semibold">Surge Multiplier</Label>
                <p className="text-xs text-[#9c9db7] mb-3">Price multiplier during peak demand (1.0 = no surge)</p>
                <Input data-testid="qc-surge" type="number" step="0.1" value={config.surge_multiplier} onChange={e => setConfig(c => ({ ...c, surge_multiplier: parseFloat(e.target.value) || 1.0 }))} className="max-w-[200px]" />
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
